import { useLayoutEffect, useRef } from 'react';
import { ProvideClassificationIcon } from '../../../icons/SvgIcons';

type ProvideClarificationProps = {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  disabled: boolean;
  loading: boolean;
  maxTextareaHeight?: number;
};

const MAX_CLARIFICATION_LENGTH = 4000;
const numberFormatter = new Intl.NumberFormat('en-US');

const ProvideClarification = ({
  value,
  onChange,
  onSubmit,
  disabled = false,
  loading = false,
  maxTextareaHeight = 60
}: ProvideClarificationProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const canSubmit = value.trim().length > 0 && !disabled && !loading;
  const characterCount = numberFormatter.format(value.length);
  const maxCharacterCount = numberFormatter.format(MAX_CLARIFICATION_LENGTH);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    textarea.style.minHeight = '60px';

    const nextHeight = Math.min(textarea.scrollHeight, maxTextareaHeight);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY =
      textarea.scrollHeight > maxTextareaHeight ? 'auto' : 'hidden';
  }, [maxTextareaHeight, value]);

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit();
  };

  return (
    <div className="flex gap-3 px-4 py-3 w-full rounded-t-[24px] bg-[linear-gradient(90deg,_#F0F4FD_0.83%,_#ADBDF4_100%)]">
      <ProvideClassificationIcon />
      <div className="flex flex-col gap-3 w-full">
        <div className="flex justify-between ">
          <div className="flex flex-col items-start  gap-0.5">
            <p className="text-sm text-purple-900 font-medium">
              Provide Clarification
            </p>
            <p className="text-xs leading-3 text-purple-600">
              Select a predefined option above, or type your answer directly
              below to refine the assessment.
            </p>
          </div>
          <div className="text-xs leading-4 text-purple-700 pointer-events-none mt-auto">
            {characterCount}/{maxCharacterCount}
          </div>
        </div>
        <div className="relative w-full">
          <textarea
            ref={textareaRef}
            className="input resize-none leading-5"
            placeholder="Type your answer or add additional context..."
            value={value}
            disabled={disabled || loading}
            maxLength={MAX_CLARIFICATION_LENGTH}
            rows={2}
            onChange={(event) => onChange(event.target.value)}
          />
        </div>
        <button
          disabled={!canSubmit}
          className="w-[63px] h-[30px] text-xs outline-none bg-white text-purple-800 font-medium rounded-full shadow-sm ml-auto cursor-pointer disabled:bg-opacity-40 disabled:text-opacity-40 disabled:cursor-default"
          onClick={handleSubmit}
        >
          {loading ? 'Sending' : 'Submit'}
        </button>
      </div>
    </div>
  );
};

export default ProvideClarification;
